import type { DecorationMode, EditorMode, Tool } from "@/state/useEditorStore";

export type EditorShortcutView = "worldMap" | "admin";

export type TilePickerShortcutRequest =
  | {
      id: number;
      kind: "escape";
    }
  | {
      id: number;
      kind: "digit";
      index: number;
    };

export type TilePickerShortcutRequestInput =
  | {
      kind: "escape";
    }
  | {
      kind: "digit";
      index: number;
    };

export type EditorShortcutAction =
  | { kind: "toggle-tile-picker" }
  | { kind: "undo" }
  | { kind: "redo" }
  | { kind: "set-editor-mode"; mode: EditorMode }
  | { kind: "tile-picker-shortcut"; request: TilePickerShortcutRequestInput }
  | { kind: "close-brush" }
  | { kind: "select-ordered-brush-index"; index: number }
  | { kind: "cycle-ordered-brush"; step: number }
  | { kind: "cycle-decoration-selection"; step: number }
  | { kind: "set-decoration-mode"; mode: DecorationMode }
  | { kind: "set-tool"; tool: Tool }
  | { kind: "open-brush-picker"; mode: "tiles" | "decorations" }
  | { kind: "toggle-campaign-notebook" }
  | { kind: "toggle-keyboard-shortcuts" };

export interface EditorShortcutContext {
  view: EditorShortcutView;
  key: string;
  code: string;
  hasModifier: boolean;
  altKey: boolean;
  shiftKey: boolean;
  repeat: boolean;
  isEditableTarget: boolean;
  tilePickerOpen: boolean;
  decorationSelectionActive: boolean;
  activeTool: Tool;
  editorMode: EditorMode;
  orderedBrushEntryCount: number;
  decorationEntryCount: number;
  mapNotesEnabled: boolean;
  campaignNotebookAvailable: boolean;
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  const editableContainer = target.closest("[contenteditable]");
  if (!(editableContainer instanceof HTMLElement)) {
    return false;
  }

  const contentEditable = editableContainer.getAttribute("contenteditable");
  return contentEditable !== "false";
}

function getDigitIndex(key: string): number | null {
  return /^[1-9]$/.test(key) ? Number(key) - 1 : null;
}

const EDITOR_MODE_CYCLE_ORDER: EditorMode[] = ["paint", "enrich"];

function getCycledEditorMode(current: EditorMode, step: number): EditorMode {
  const index = EDITOR_MODE_CYCLE_ORDER.indexOf(current);
  if (index < 0) {
    return EDITOR_MODE_CYCLE_ORDER[0];
  }
  const length = EDITOR_MODE_CYCLE_ORDER.length;
  const nextIndex = (((index + step) % length) + length) % length;
  return EDITOR_MODE_CYCLE_ORDER[nextIndex];
}

export function resolveEditorShortcut(
  context: EditorShortcutContext,
): EditorShortcutAction | null {
  if (context.view !== "worldMap" || context.repeat || context.isEditableTarget) {
    return null;
  }

  // "?" opens the shortcuts reference. On most layouts this is Shift+/ which
  // produces "?" in `event.key` directly, so accept it without requiring the
  // shift flag here. Allow "/" + shift as a fallback for keyboards where the
  // raw key is reported as "/" instead of "?".
  if (
    !context.hasModifier &&
    !context.altKey &&
    (context.key === "?" || (context.shiftKey && context.key === "/"))
  ) {
    return { kind: "toggle-keyboard-shortcuts" };
  }

  if (context.hasModifier && context.key === "k") {
    return { kind: "toggle-tile-picker" };
  }

  if (context.hasModifier && context.key === "z" && !context.shiftKey) {
    return { kind: "undo" };
  }

  if (context.hasModifier && context.key === "z" && context.shiftKey) {
    return { kind: "redo" };
  }

  if (
    !context.hasModifier &&
    !context.altKey &&
    !context.shiftKey &&
    context.code === "Tab"
  ) {
    return {
      kind: "set-editor-mode",
      mode: getCycledEditorMode(context.editorMode, 1),
    };
  }

  if (
    !context.hasModifier &&
    !context.altKey &&
    context.shiftKey &&
    context.code === "Digit1"
  ) {
    return { kind: "set-editor-mode", mode: "paint" };
  }

  if (
    !context.hasModifier &&
    !context.altKey &&
    context.shiftKey &&
    context.code === "Digit2"
  ) {
    return { kind: "set-editor-mode", mode: "enrich" };
  }

  if (context.tilePickerOpen) {
    if (context.key === "escape") {
      return {
        kind: "tile-picker-shortcut",
        request: { kind: "escape" },
      };
    }

    const digitIndex = getDigitIndex(context.key);
    if (digitIndex !== null) {
      return {
        kind: "tile-picker-shortcut",
        request: { kind: "digit", index: digitIndex },
      };
    }

    return null;
  }

  if (context.decorationSelectionActive && context.key === "escape") {
    return { kind: "set-tool", tool: "pointer" };
  }

  if (context.decorationSelectionActive && context.decorationEntryCount > 1) {
    if (!context.altKey && context.key === "q") {
      return { kind: "cycle-decoration-selection", step: -1 };
    }

    if (!context.altKey && context.key === "e") {
      return { kind: "cycle-decoration-selection", step: 1 };
    }
  }

  if (context.activeTool === "tile-brush" && context.orderedBrushEntryCount > 0) {
    if (context.key === "escape") {
      return { kind: "close-brush" };
    }

    const digitIndex = getDigitIndex(context.key);
    if (digitIndex !== null && digitIndex < context.orderedBrushEntryCount) {
      return { kind: "select-ordered-brush-index", index: digitIndex };
    }

    if (!context.altKey && context.key === "q") {
      return { kind: "cycle-ordered-brush", step: -1 };
    }

    if (!context.altKey && context.key === "e") {
      return { kind: "cycle-ordered-brush", step: 1 };
    }
  }

  if (context.activeTool !== "pointer" && context.key === "escape") {
    return { kind: "set-tool", tool: "pointer" };
  }

  if (context.hasModifier || context.altKey) {
    return null;
  }

  if (context.decorationSelectionActive && context.key === "1") {
    return { kind: "set-decoration-mode", mode: "single" };
  }

  if (context.decorationSelectionActive && context.key === "2") {
    return { kind: "set-decoration-mode", mode: "brush" };
  }

  if (context.decorationSelectionActive && context.key === "3") {
    return { kind: "set-decoration-mode", mode: "erase" };
  }

  if (context.editorMode === "paint" && context.key === "1") {
    return { kind: "set-tool", tool: "pointer" };
  }

  if (context.editorMode === "paint" && context.key === "2") {
    return { kind: "open-brush-picker", mode: "tiles" };
  }

  if (context.editorMode === "paint" && context.key === "3") {
    return { kind: "open-brush-picker", mode: "decorations" };
  }

  if (context.editorMode === "paint" && context.key === "4") {
    return { kind: "set-tool", tool: "eyedropper" };
  }

  if (context.editorMode === "enrich" && context.key === "1") {
    return { kind: "set-tool", tool: "pointer" };
  }

  if (context.editorMode === "enrich" && context.key === "2") {
    return { kind: "set-tool", tool: "text" };
  }

  if (context.editorMode === "enrich" && context.key === "3" && context.mapNotesEnabled) {
    return { kind: "set-tool", tool: "map-note" };
  }

  if (context.editorMode === "enrich" && context.key === "4" && context.campaignNotebookAvailable) {
    return { kind: "toggle-campaign-notebook" };
  }

  if (context.key === "v") {
    return { kind: "set-tool", tool: "pointer" };
  }

  if (context.editorMode === "paint" && context.key === "t") {
    return { kind: "open-brush-picker", mode: "tiles" };
  }

  if (context.editorMode === "paint" && context.key === "d") {
    return { kind: "open-brush-picker", mode: "decorations" };
  }

  if (context.editorMode === "paint" && context.key === "e") {
    return { kind: "set-tool", tool: "eyedropper" };
  }

  if (context.key === "t") {
    return { kind: "set-tool", tool: "text" };
  }

  if (context.mapNotesEnabled && context.key === "p") {
    return { kind: "set-tool", tool: "map-note" };
  }

  if (context.key === "n") {
    return { kind: "toggle-campaign-notebook" };
  }

  return null;
}
